import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_5ca6c876 from 'nuxt_plugin_sentryserver_5ca6c876' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_6bf32a3d from 'nuxt_plugin_sentryclient_6bf32a3d' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_plugin_70feb96b from 'nuxt_plugin_plugin_70feb96b' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_plugin_6a6c0b3a from 'nuxt_plugin_plugin_6a6c0b3a' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_44462aae from 'nuxt_plugin_cookieuniversalnuxt_44462aae' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_vuetifydialog_c3895420 from 'nuxt_plugin_vuetifydialog_c3895420' // Source: ./vuetify-dialog.js (mode: 'client')
import nuxt_plugin_markdownit_96acb464 from 'nuxt_plugin_markdownit_96acb464' // Source: ./markdown-it.js (mode: 'all')
import nuxt_plugin_pluginclient_045fd767 from 'nuxt_plugin_pluginclient_045fd767' // Source: ./content/plugin.client.js (mode: 'client')
import nuxt_plugin_pluginserver_6a1948ef from 'nuxt_plugin_pluginserver_6a1948ef' // Source: ./content/plugin.server.js (mode: 'server')
import nuxt_plugin_workbox_2d5ca5dd from 'nuxt_plugin_workbox_2d5ca5dd' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_25568020 from 'nuxt_plugin_metaplugin_25568020' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_iconplugin_1c073538 from 'nuxt_plugin_iconplugin_1c073538' // Source: ./pwa/icon.plugin.js (mode: 'all')
import nuxt_plugin_axios_ea9925fe from 'nuxt_plugin_axios_ea9925fe' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_vuecopy_15152ab2 from 'nuxt_plugin_vuecopy_15152ab2' // Source: ../plugins/vue-copy.js (mode: 'all')
import nuxt_plugin_vueanalytics_22d4fb8c from 'nuxt_plugin_vueanalytics_22d4fb8c' // Source: ../plugins/vue-analytics.js (mode: 'client')
import nuxt_plugin_vueics_6122784e from 'nuxt_plugin_vueics_6122784e' // Source: ../plugins/vue-ics.js (mode: 'client')
import nuxt_plugin_vuexpersist_2d22f7ac from 'nuxt_plugin_vuexpersist_2d22f7ac' // Source: ../plugins/vuex-persist.js (mode: 'client')
import nuxt_plugin_vuesvggauge_6b1a2282 from 'nuxt_plugin_vuesvggauge_6b1a2282' // Source: ../plugins/vue-svg-gauge.js (mode: 'client')
import nuxt_plugin_tenant_321dd599 from 'nuxt_plugin_tenant_321dd599' // Source: ../plugins/tenant.js (mode: 'all')
import nuxt_plugin_axios_397e53b5 from 'nuxt_plugin_axios_397e53b5' // Source: ../plugins/axios.js (mode: 'all')
import nuxt_plugin_autoresumeclient_1b594696 from 'nuxt_plugin_autoresumeclient_1b594696' // Source: ../plugins/auto-resume.client.js (mode: 'client')
import nuxt_plugin_navigationguard_355aea57 from 'nuxt_plugin_navigationguard_355aea57' // Source: ../plugins/navigation-guard.js (mode: 'client')
import nuxt_plugin_chunkrecoveryclient_efd90ef2 from 'nuxt_plugin_chunkrecoveryclient_efd90ef2' // Source: ../plugins/chunk-recovery.client.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"titleTemplate":"%s","title":"MyUSE","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"MyUSE (My Understanding Substance Use Experiences) is a support and harm-reduction web application, providing support to higher-education students who want to understand their illicit substance use experiences and access relevant resources."}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"},{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Fcdn.jsdelivr.net\u002Fnpm\u002F@mdi\u002Ffont@5.x\u002Fcss\u002Fmaterialdesignicons.min.css"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_5ca6c876 === 'function') {
    await nuxt_plugin_sentryserver_5ca6c876(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_6bf32a3d === 'function') {
    await nuxt_plugin_sentryclient_6bf32a3d(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_70feb96b === 'function') {
    await nuxt_plugin_plugin_70feb96b(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_6a6c0b3a === 'function') {
    await nuxt_plugin_plugin_6a6c0b3a(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_44462aae === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_44462aae(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuetifydialog_c3895420 === 'function') {
    await nuxt_plugin_vuetifydialog_c3895420(app.context, inject)
  }

  if (typeof nuxt_plugin_markdownit_96acb464 === 'function') {
    await nuxt_plugin_markdownit_96acb464(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_pluginclient_045fd767 === 'function') {
    await nuxt_plugin_pluginclient_045fd767(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_pluginserver_6a1948ef === 'function') {
    await nuxt_plugin_pluginserver_6a1948ef(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_2d5ca5dd === 'function') {
    await nuxt_plugin_workbox_2d5ca5dd(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_25568020 === 'function') {
    await nuxt_plugin_metaplugin_25568020(app.context, inject)
  }

  if (typeof nuxt_plugin_iconplugin_1c073538 === 'function') {
    await nuxt_plugin_iconplugin_1c073538(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_ea9925fe === 'function') {
    await nuxt_plugin_axios_ea9925fe(app.context, inject)
  }

  if (typeof nuxt_plugin_vuecopy_15152ab2 === 'function') {
    await nuxt_plugin_vuecopy_15152ab2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueanalytics_22d4fb8c === 'function') {
    await nuxt_plugin_vueanalytics_22d4fb8c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueics_6122784e === 'function') {
    await nuxt_plugin_vueics_6122784e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexpersist_2d22f7ac === 'function') {
    await nuxt_plugin_vuexpersist_2d22f7ac(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuesvggauge_6b1a2282 === 'function') {
    await nuxt_plugin_vuesvggauge_6b1a2282(app.context, inject)
  }

  if (typeof nuxt_plugin_tenant_321dd599 === 'function') {
    await nuxt_plugin_tenant_321dd599(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_397e53b5 === 'function') {
    await nuxt_plugin_axios_397e53b5(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_autoresumeclient_1b594696 === 'function') {
    await nuxt_plugin_autoresumeclient_1b594696(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_navigationguard_355aea57 === 'function') {
    await nuxt_plugin_navigationguard_355aea57(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_chunkrecoveryclient_efd90ef2 === 'function') {
    await nuxt_plugin_chunkrecoveryclient_efd90ef2(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
